import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import * as S from './ActivitiesPreviewDepricated.styles';
import { formatReviewsCount, formatReviewsRating } from '@/common/app/utils/numberUtils';
import { IActivityWithAvailability } from '@/common/domain/Merge.domain';
import { Rating } from '../Rating';
import { PreviewImagesGallery } from '../ImagesGallery/ImagesGallery';
import { useInView } from 'react-intersection-observer';
import { impressionInterceptor } from '@/shared/Analytics/GA4/ImpressionInterceptor';
import { AnalyticsInstance } from '@/common/service/analytics';
import { createCategoryNameFromListName } from '@/common/service/analytics/creators';
import { TSlug } from '@/common/domain/Slug.domain';
import Link from 'next/link';
import { BookedCount } from '@/common/ui/styled/BookedCount/BookedCount';
import {
    convertActivityDataToAnalyticsData,
    wraitDetasilsData,
} from '@/shared/Analytics/GA4/helpers';
import { trackSelectItem } from '@/shared/Analytics/GA4';
import { useAttractionsContext } from '@/common/app/contexts';

const DESCRIPTION_LIMIT = 100;

type ActivitiesPreviewProps = {
    activity: IActivityWithAvailability;
    position: number;
    userImages?: TSlug[];
    listName?: string;
    listId?: string;
    disabled?: boolean;
    listNameGA3?: string;
    isLastItem?: boolean;
    innerRef?: (node?: Element | null | undefined) => void;
    isFromSearch?: boolean;
};

const ActivitiesPreview = ({
    activity,
    disabled,
    listName,
    listId,
    position,
    listNameGA3,
    userImages,
    innerRef,
}: //isFromSearch,
// eslint-disable-next-line sonarjs/cognitive-complexity
ActivitiesPreviewProps): ReactElement => {
    const [readMore, setReadMore] = useState(false);
    const { state } = useAttractionsContext();
    const { availability } = state;

    const reviewsCountWithComma = formatReviewsCount(activity.rate_count);
    const reviewRating = formatReviewsRating(activity.rating);

    const scrollRef = useRef<HTMLDivElement>(null);
    const currentListId = listId
        ? listId
        : typeof window !== 'undefined'
        ? window.location.pathname
        : '';

    const currentactivityData: IActivityWithAvailability = useMemo(() => {
        if (!availability[activity.id]) return activity;
        return {
            ...activity,
            price: availability[activity.id].price,
            price_subunits: availability[activity.id].price_subunits,
            price_strike_out: availability[activity.id].price_strike_out || null,
            price_strike_out_subunits: availability[activity.id].price_strike_out_subunits || null,
            days_availability: availability[activity.id].days_availability,
        };
    }, [availability, activity]);

    const activityData = convertActivityDataToAnalyticsData(
        currentactivityData,
        position,
        listName || '',
        currentListId
    );

    const [ref] = useInView({
        triggerOnce: true,
        onChange: (inView) => {
            if (inView) {
                const categoryName = listNameGA3 ? createCategoryNameFromListName(listNameGA3) : '';

                impressionInterceptor.addImpression(
                    AnalyticsInstance.creators.createImpressionFromArticlePreview({
                        listName: listNameGA3 || '',
                        categoryName,
                        position,
                    })(currentactivityData),
                    true,
                    activityData
                );
            }
        },
    });

    const item_list_id = listId || typeof window === 'undefined' ? '' : window.location.pathname;

    const readMoreEvent = useCallback(
        (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            if (readMore) {
                scrollRef.current?.scrollTo(0, 0);
            }
            setReadMore(!readMore);
            e.stopPropagation();
            e.preventDefault();
        },
        [readMore]
    );

    const onDisabledClick = useCallback((e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();
    }, []);

    const descriptionText = activity.description || activity.previewDescription || '';
    const longRead = descriptionText.length > DESCRIPTION_LIMIT;

    const clickHandler = () => {
        const item_list_name = listName || '';
        wraitDetasilsData({
            index: position + 1,
            item_list_name,
            item_list_id,
        });

        trackSelectItem({
            item_list_name,
            item_list_id,
            items: [
                {
                    ...activityData,
                },
            ],
        });
    };

    return (
        <Link href={activity.url.toLowerCase()} passHref prefetch={false}>
            <S.Container ref={innerRef} data-test-id="Activity_card" onClick={clickHandler}>
                {disabled && <S.Disabled onClick={onDisabledClick} />}
                <PreviewImagesGallery
                    activity={currentactivityData}
                    disabled={disabled}
                    position={position + 1}
                    item_list_name={listName || ''}
                    item_list_id={item_list_id}
                    userImages={userImages}
                    withPaddings
                    event
                />
                <S.ContentContainer isActive={readMore} ref={ref}>
                    <S.Title>{activity.name}</S.Title>
                    <S.DurationWrapper>
                        {!!activity.rate_count && (
                            <>
                                <S.RatingText data-test-id="Rating_text">
                                    {reviewRating}
                                </S.RatingText>
                                <Rating
                                    rating={activity.rating_stars}
                                    data-test-id="Rating_start"
                                />
                                <S.RatingCount data-test-id="Rating_text">
                                    ({reviewsCountWithComma})
                                </S.RatingCount>
                            </>
                        )}
                        {!activity.rate_count && <S.BeFirst>Be the first to review!</S.BeFirst>}
                        <S.Duration>
                            {!!activity.duration && (
                                <>
                                    <S.ClockIcon />
                                    {activity.duration}
                                </>
                            )}
                        </S.Duration>
                    </S.DurationWrapper>
                    <S.Description
                        data-test-id="Activity_description"
                        readMore={readMore}
                        ref={scrollRef}
                    >
                        {activity.special_status.mobile && (
                            <S.SpecialStatus>{activity.special_status.mobile}</S.SpecialStatus>
                        )}
                        {descriptionText}
                    </S.Description>
                    {longRead && (
                        <S.ReadMore
                            onClick={readMoreEvent}
                            isActive={readMore}
                            data-test-id="Read_more_activity_card"
                        >
                            {readMore ? 'See less' : 'Read more'}
                            <S.Arrow isActive={readMore} />
                        </S.ReadMore>
                    )}
                    <BookedCount recentPurchased={activity.recent_purchased} isFire />
                </S.ContentContainer>
            </S.Container>
        </Link>
    );
};

export { ActivitiesPreview };
